import {
  Dispatch,
  FC,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { X, Plus, CalendarIcon, Loader2, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { addDays, format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import AutocompleteLocation from "./AutocompleteLocation";
import CurrencySelect from "@/components/input/CurrencySelect";
import { DriverInfo } from "./DriverInfo";
import { FixedRatesDialog } from "./FixedRatesDialog";
import { RouteAreaRate } from "@/utils/routeAreaRate";
import {
  CalculateRateDto,
  CalculateRateResponseDto,
} from "@/interface/fixedRates";
import { usePermissions } from "react-admin";
import { seperateDateAndTime } from "@/utils/formChange";
import axiosInstance from "@/utils/axiosInstance";
import { fetchClientFromPhone } from "@/utils/functions";
import { toast } from "sonner";
import ScheduledOrders from "./ScheduledOrders";
import { useSipContext } from "@/context/SipContext";
import { formatPhoneNumber } from "@/utils/phoneNumber";
import { useOrderData } from "@/taxi-dispatch/orders/hooks/useOrderData";
import { BaseSelect } from "@/components/input/CustomInputs";
import { useUserContext } from "@/context/UserContext";

interface Location {
  id?: number;
  text: string;
  lat: number;
  lng: number;
}

interface Order {
  pickupLocation: Location | null;
  pickupMapLink: string;
  dropoffLocation: Location | null;
  dropoffMapLink: string;
  fixedPrice: boolean;
  scheduled: boolean;
  date: string | null;
  time: string;
  clientName: string;
  phoneNumber: string;
  flightNumber: string;
  fare: number | string;
  notes: string;
  currency: string;
  hours: number;
  minutes: number;
  source: string;
  commission_partner_id: number | null;
}

interface NewOrderProps {
  ordersData: (order: Order) => void;
  pickupLocationRef: any;
  dropoffLocationRef: any;
  pickupLocation: Location;
  setPickupLocation: Dispatch<SetStateAction<Location>>;
  dropoffLocation: Location;
  setDropoffLocation: Dispatch<SetStateAction<Location>>;
  isNewOrderOpen: boolean;
  setNewOrderOpen: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  selectedVehicle: any;
  setCenter: Dispatch<SetStateAction<{ lat: number; lng: number }>>;
  setFocus: Dispatch<SetStateAction<"pickup" | "dropoff" | null>>;
}

const clientNameCache = new Map<string, string>();

const NewOrder: FC<NewOrderProps> = ({
  ordersData,
  pickupLocationRef,
  dropoffLocationRef,
  pickupLocation,
  setPickupLocation,
  dropoffLocation,
  setDropoffLocation,
  isNewOrderOpen,
  setNewOrderOpen,
  isLoading,
  selectedVehicle,
  setCenter,
  setFocus,
}) => {
  const { flowControl } = useSipContext();
  const permissions = usePermissions();
  const { partners } = useOrderData();
  const { decodedToken } = useUserContext();

  const [pickupMapLink] = useState<string>("");
  const [dropoffMapLink] = useState<string>("");
  const [fixedPrice, setFixedPrice] = useState<boolean>(false);
  const [isWhatsapp, setWhatsapp] = useState<boolean>(false);
  const [scheduled, setScheduled] = useState<boolean>(false);
  const [hours, setHours] = useState<any>("");
  const [minutes, setMinutes] = useState<any>("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [clientName, setClientName] = useState("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<string>("");
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [fare, setFare] = useState<number | string>("");
  const [notes, setNotes] = useState<string>("");
  const [formProp, setFormProp] = useState<{ commission_partner_id: string }>({
    commission_partner_id: "",
  });

  const [currency, setCurrency] = useState("ALL");
  const [fixedRatesDialogOpen, setFixedRatesDialogOpen] = useState(false);
  const [pointLocations, setPointLocations] = useState<Location[]>([]);
  const [routeAreaRates, setRouteAreaRates] = useState<RouteAreaRate[]>([]);
  const [activeTab, setActive] = useState<"new_order" | "scheduled">(
    "new_order",
  );

  const [appliedRoute, setAppliedRoute] = useState<{
    pickup: string;
    dropoff: string;
  } | null>(null);
  const [isCalculatingRate, setIsCalculatingRate] = useState<boolean>(false);

  const getPickupLocation = (data: Location) => {
    setPickupLocation(data);
    setCenter({ lat: data.lat, lng: data.lng });
  };

  const getDropoffLocation = (data: Location) => {
    setDropoffLocation(data);
    setCenter({ lat: data.lat, lng: data.lng });
  };

  useEffect(() => {
    const currDate = new Date();

    const dateAndTimeSeperated = seperateDateAndTime(currDate.toISOString());

    setDate(new Date(dateAndTimeSeperated.datePart));
    setTime(dateAndTimeSeperated.timePart);
  }, []);

  // Auto-fill phone number from active call when opening new order
  useEffect(() => {
    if (isNewOrderOpen && flowControl?.activeCall) {
      const activeCall = flowControl.activeCall;
      const phone = activeCall.remote_identity?.uri?.user || "";

      if (phone) {
        console.log("Auto-filling phone number from active call:", phone);
        // Format phone number with '+' prefix
        setPhoneNumber(formatPhoneNumber(phone));

        // Fetch client name from phone number
        fetchClientFromPhone(clientNameCache, setClientName, phone);
      }
    }
  }, [isNewOrderOpen, flowControl?.activeCall, flowControl?.activeCall?.id]);

  // Fetch point locations from route areas
  useEffect(() => {
    const fetchPointLocations = async () => {
      try {
        const response = await axiosInstance.get("/route-areas");
        const areas = response.data || [];
        const points = areas
          .filter(
            (area: any) =>
              area.type === "POINT" &&
              Array.isArray(area.coordinates) &&
              area.coordinates.length > 0,
          )
          .map((area: any) => {
            return {
              id: area.id,
              text: area.name,
              lat: area.coordinates[0].lat,
              lng: area.coordinates[0].lng,
            };
          });

        setPointLocations(points);
      } catch (error) {
        console.error("Error fetching point locations:", error);
      }
    };

    fetchPointLocations();
  }, []);

  // Fetch route area rates once
  useEffect(() => {
    const fetchRouteAreaRates = async () => {
      try {
        const response = await axiosInstance.get("/route-area-rates");
        const rates = (response.data || []).map((rate: any) => ({
          ...rate,
          pickup_area: {
            ...rate.pickup_area,
            coordinates: Array.isArray(rate.pickup_area.coordinates)
              ? rate.pickup_area.coordinates
              : JSON.parse(rate.pickup_area.coordinates),
          },
          dropoff_area: {
            ...rate.dropoff_area,
            coordinates: Array.isArray(rate.dropoff_area.coordinates)
              ? rate.dropoff_area.coordinates
              : JSON.parse(rate.dropoff_area.coordinates),
          },
        }));

        setRouteAreaRates(rates);
      } catch (error) {
        console.error("Error fetching route area rates:", error);
        setRouteAreaRates([]);
      }
    };

    fetchRouteAreaRates();
  }, []);

  // Calculate fixed rate when fixedPrice is checked and both locations are selected
  useEffect(() => {
    if (
      !fixedPrice ||
      !pickupLocation ||
      !dropoffLocation ||
      routeAreaRates.length === 0
    ) {
      setFare("");
      setAppliedRoute(null);
      return;
    }

    const calculateRate = async () => {
      setIsCalculatingRate(true);
      try {
        const requestDto: CalculateRateDto = {
          pickup: {
            lat: pickupLocation.lat,
            lng: pickupLocation.lng,
            name: pickupLocation.text,
          },
          dropoff: {
            lat: dropoffLocation.lat,
            lng: dropoffLocation.lng,
            name: dropoffLocation.text,
          },
        };

        const response = await axiosInstance.post<CalculateRateResponseDto>(
          "/route-area-rates/calculate",
          requestDto,
        );

        const result = response.data;

        if (result.recommendedRate) {
          setFare(result.recommendedRate.price);
          setCurrency(result.recommendedRate.currency);
          toast.success(
            `Fixed rate found: ${result.recommendedRate.price} ${result.recommendedRate.currency}`,
          );
          setAppliedRoute({
            pickup: result.pickup.name || "Pickup Area",
            dropoff: result.dropoff.name || "Dropoff Area",
          });
        } else if (result.matchingRates && result.matchingRates.length > 0) {
          // If no recommended rate but matching rates exist, use the first one
          const firstMatch = result.matchingRates[0];
          setFare(firstMatch.price);
          setCurrency(firstMatch.currency);
          toast.success(
            `Fixed rate found: ${firstMatch.price} ${firstMatch.currency}`,
          );
          setAppliedRoute({
            pickup: firstMatch.pickup_area.name,
            dropoff: firstMatch.dropoff_area.name,
          });
        } else {
          console.log("No fixed rate found for this route");
          setFare("");
          setAppliedRoute(null);
        }
      } catch (error) {
        console.error("Error calculating fixed rate:", error);
        setFare("");
        setAppliedRoute(null);
        toast.error("Failed to calculate fixed rate");
      } finally {
        setIsCalculatingRate(false);
      }
    };

    calculateRate();
  }, [pickupLocation, dropoffLocation, fixedPrice]);

  const handleAddOrder = () => {
    if (!pickupLocation.text) {
      toast("Pickup location is missing");
      return;
    }

    const order: Order = {
      pickupLocation,
      pickupMapLink,
      dropoffLocation,
      dropoffMapLink,
      fixedPrice,
      scheduled,
      date: date ? format(date, "yyyy-MM-dd") : null,
      time,
      clientName,
      phoneNumber,
      flightNumber,
      fare,
      notes,
      currency,
      hours,
      minutes,
      source: isWhatsapp ? "WHATSAPP" : "DISPATCH",
      commission_partner_id: Number(formProp.commission_partner_id) || null,
    };

    try {
      ordersData(order);

      setPickupLocation({
        text: "",
        lat: 0,
        lng: 0,
      });
      setDropoffLocation({
        text: "",
        lat: 0,
        lng: 0,
      });
      setClientName("");
      setPhoneNumber("");
      setHours("");
      setMinutes("");
      setFlightNumber("");
      setNotes("");
      setFixedPrice(false);
      setScheduled(false);
      setWhatsapp(false);

      const currDate = new Date();

      const dateAndTimeSeperated = seperateDateAndTime(currDate.toISOString());

      setDate(new Date(dateAndTimeSeperated.datePart));
      setTime(dateAndTimeSeperated.timePart);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleClose = () => {
    setNewOrderOpen(false);
  };

  const handleRateSelect = (rate: RouteAreaRate) => {
    setFare(rate.price);
    setCurrency(rate.currency);
    setAppliedRoute({
      pickup: rate.pickup_area.name,
      dropoff: rate.dropoff_area.name,
    });
  };

  return (
    <div>
      <div className="p-4 border rounded-xl shadow-lg bg-background max-h-[85vh] overflow-y-auto relative z-10 w-[350px]">
        <button
          type="button"
          onClick={handleClose}
          className="cursor-pointer absolute top-4 right-3 text-gray-500 hover:text-red-500"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-end gap-x-6 mb-2">
          <Button
            variant={activeTab === "new_order" ? "secondary" : "outline"}
            onClick={() => setActive("new_order")}
          >
            New order
          </Button>
          <Button
            variant={activeTab === "scheduled" ? "secondary" : "outline"}
            onClick={() => setActive("scheduled")}
          >
            {" "}
            Scheduled{" "}
          </Button>
        </div>
        {activeTab === "new_order" && (
          <div className="pt-2 pb-2">
            <div>
              <AutocompleteLocation
                placeholder="Pickup location"
                location={getPickupLocation}
                inputRef={pickupLocationRef}
                selectedLocation={pickupLocation}
                optionalLocations={pointLocations}
                onChange={(e: FormEvent<HTMLInputElement>) => {
                  if (!e.currentTarget.value)
                    setPickupLocation({
                      text: "",
                      lat: 0,
                      lng: 0,
                    });
                }}
                onFocus={() => setFocus("pickup")}
              />
              <p className="text-[10px] ml-1 mb-2 text-gray-400">
                or paste from google maps
              </p>
            </div>
            <div className="flex items-center gap-x-2">
              <div className="flex items-center space-x-2 pt-1 pb-4 pl-1">
                <Checkbox
                  id="fixedPrice"
                  checked={fixedPrice}
                  onCheckedChange={(checked) =>
                    setFixedPrice(checked as boolean)
                  }
                />
                <label htmlFor="fixed" className="text-sm">
                  Fixed Price
                </label>
                {fixedPrice && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setFixedRatesDialogOpen(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-semibold ml-2"
                  >
                    Select Rate
                  </a>
                )}
              </div>
              <div className="flex items-center space-x-2 pt-1 pb-4 pl-1">
                <Checkbox
                  id="whatsapp"
                  checked={isWhatsapp}
                  onCheckedChange={(checked) => setWhatsapp(checked as boolean)}
                />
                <label htmlFor="fixed" className="text-sm">
                  Whatsapp
                </label>
              </div>
            </div>
            <div>
              <AutocompleteLocation
                placeholder="Dropoff location"
                location={getDropoffLocation}
                inputRef={dropoffLocationRef}
                required={false}
                selectedLocation={dropoffLocation}
                optionalLocations={pointLocations}
                onChange={(e: FormEvent<HTMLInputElement>) => {
                  if (!e.currentTarget.value)
                    setDropoffLocation({
                      text: "",
                      lat: 0,
                      lng: 0,
                    });
                }}
                onFocus={() => setFocus("dropoff")}
              />
              <p className="text-[10px] ml-1 mb-2 text-gray-400">
                or paste from google maps
              </p>
            </div>
            <div className="pt-1">
              <h3 className="text-[12px] font-semibold mb-2 pl-1">
                Date & Time
              </h3>
              <div className="flex items-center gap-2">
                <div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="flex w-auto flex-col space-y-2 p-2"
                    >
                      <Select
                        onValueChange={(value) =>
                          setDate(addDays(new Date(), parseInt(value)))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="0">Today</SelectItem>
                          <SelectItem value="1">Tomorrow</SelectItem>
                          <SelectItem value="3">In 3 days</SelectItem>
                          <SelectItem value="7">In a week</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="rounded-md border">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  type="time"
                  placeholder="Time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 pt-4 pl-1">
              <Checkbox
                id="scheduled"
                checked={scheduled}
                onCheckedChange={() => setScheduled(!scheduled)}
              />
              <label htmlFor="scheduled" className="text-sm">
                Add to scheduled
              </label>
            </div>
            {scheduled && (
              <div className="pt-4">
                <h3 className="text-[12px] font-semibold pl-1">
                  Set reminder time
                </h3>
                {(hours || minutes) && (
                  <span className="text-[11px] text-gray-500 font-medium pl-1">
                    You will get a reminder {hours || 0}h {minutes || 0}min
                    before order event
                  </span>
                )}
                <div className="grid sm:grid-cols-2 gap-2 mt-1">
                  <Input
                    type="number"
                    placeholder="Hours"
                    min={0}
                    max={23}
                    value={hours}
                    onChange={(e) => {
                      if (+e.target.value > 23) return;
                      setHours(+e.target.value || "");
                    }}
                  />
                  <Input
                    type="number"
                    placeholder="Minutes"
                    min={0}
                    max={59}
                    value={minutes}
                    onChange={(e) => {
                      if (+e.target.value > 59) return;
                      setMinutes(+e.target.value || "");
                    }}
                  />
                </div>
              </div>
            )}
            <div className="pt-4">
              <h3 className="text-[12px] font-semibold mb-2 pl-1">
                Client Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  type="text"
                  placeholder="Client name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                />
                <Input
                  type="tel"
                  placeholder="Phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
              {permissions.data.settings_type == "taxi" && fixedPrice && (
                <div className="space-y-2 mt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder={
                          isCalculatingRate ? "Calculating..." : "Order price"
                        }
                        value={fare}
                        disabled={isCalculatingRate}
                        onChange={(e) => {
                          setFare(e.target.value);
                          setAppliedRoute(null); // Clear applied route when user manually changes price
                        }}
                      />
                      {isCalculatingRate && (
                        <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin" />
                      )}
                    </div>
                    <div>
                      <CurrencySelect
                        value={currency}
                        setValue={(newCurrency: string) => {
                          setCurrency(newCurrency);
                          setAppliedRoute(null); // Clear applied route when user manually changes currency
                        }}
                      />
                    </div>
                  </div>
                  {appliedRoute && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-md">
                      <Info className="h-4 w-4" />
                      <span>
                        Fixed rate from {appliedRoute.pickup} to{" "}
                        {appliedRoute.dropoff}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {partners.length > 0 &&
                decodedToken?.permissions.user_role !==
                  "commission_partner" && (
                  <div className="mt-4">
                    <h3 className="text-[12px] font-semibold mb-2 pl-1">
                      Commission partner
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <BaseSelect
                        id="commission_partner_id"
                        resource="referral-agents"
                        value={formProp.commission_partner_id}
                        setValue={setFormProp}
                        dataProp="name"
                      />
                    </div>
                  </div>
                )}
            </div>
            <div className="mt-4">
              <h3 className="text-[12px] font-semibold mb-2 pl-1">
                Flight Number
              </h3>
              <div className="grid grid-cols-1 gap-2">
                <Input
                  type="text"
                  placeholder="Flight Number"
                  value={flightNumber}
                  onChange={(e) => setFlightNumber(e.target.value)}
                />
              </div>
            </div>
            <div className="pt-4">
              <h3 className="text-[12px] font-semibold mb-2 pl-1">Notes</h3>
              <Textarea
                placeholder="Additional notes or instructions..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
              />
              <div className="flex justify-end">
                <p className="text-gray-500 text-sm">{notes.length} / 2000</p>
              </div>
            </div>
            {permissions.data.settings_type == "taxi" && (
              <div className="pt-4">
                <DriverInfo markerData={selectedVehicle} />
              </div>
            )}
            <div className="pt-3">
              <Button
                onClick={handleAddOrder}
                className="w-full"
                disabled={isLoading}
              >
                <Plus size={16} />
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Add Order"
                )}
              </Button>
            </div>
          </div>
        )}
        {activeTab === "scheduled" && <ScheduledOrders setCenter={setCenter} />}
      </div>

      {/* Fixed Rates Dialog */}
      <FixedRatesDialog
        open={fixedRatesDialogOpen}
        onOpenChange={setFixedRatesDialogOpen}
        onRateSelect={handleRateSelect}
        rates={routeAreaRates}
        pickupLocation={pickupLocation}
        dropoffLocation={dropoffLocation}
      />
    </div>
  );
};

export default NewOrder;
